import React from 'react';
import { Animated, EasingFunction, ViewProperties } from 'react-native';
import { DISABLE_ANIMATIONS, WARN_LOOPING_ANIMATIONS } from '@shared/styles';
import * as AnimationUtil from './util';

type MBAnimationOutputRange<T> = { from: T; to: T };

export interface MBAnimationProps extends ViewProperties {
  duration?: number;
  delay?: number;
  loop?: boolean;
  easing?: EasingFunction;
  type?: 'timing' | 'spring';

  /*
        If set, the animation won't play until the prop updates
        to true. When the prop is set again, the animation will
        go back to its start position. This is useful eg for
        hover effects when we want to show an animated transition
        based on some changing state.
    */
  disabled?: boolean;

  opacity?: MBAnimationOutputRange<number>;
  scale?: MBAnimationOutputRange<number>;
  scaleX?: MBAnimationOutputRange<number>;
  scaleY?: MBAnimationOutputRange<number>;
  rotate?: MBAnimationOutputRange<number | string>;
  translateX?: MBAnimationOutputRange<number>;
  translateY?: MBAnimationOutputRange<number>;
  width?: MBAnimationOutputRange<number | string>;
  height?: MBAnimationOutputRange<number | string>;
  backgroundColor?: MBAnimationOutputRange<string>;
  animationClass?: string;
}

/*
    This component wraps the Animated API to make it simple to
    animate different types of props. All animations (eg opacity,
    rotate, scale) can simply be written as props and this
    component takes care of the rest.
*/
export class MBAnimation extends React.PureComponent<MBAnimationProps> {
  animation = new Animated.Value(0);
  loopingAnimation: Animated.CompositeAnimation | null = null;

  // We want to use native driver on native when all props we're
  // animating are suppported.
  useNativeDriver(): boolean {
    return false;
  }

  componentDidMount() {
    if (!this.props.disabled) {
      this.animate({ toValue: 1 });
    }
  }

  componentWillUnmount() {
    if (!!this.loopingAnimation) {
      this.loopingAnimation.stop();
    }
  }

  // TODO(fant): replace with getDerivedStateFromProps
  // oxlint-disable-next-line react/no-unsafe
  UNSAFE_componentWillUpdate(nextProps: MBAnimationProps) {
    if (this.props.disabled !== nextProps.disabled) {
      this.animate({ toValue: nextProps.disabled ? 0 : 1 });
    }
  }

  animate({ toValue }: { toValue: number }) {
    // If we disable animations (eg because of Storybook screenshots),
    // make the animation appear in its end state right away.
    if (DISABLE_ANIMATIONS) {
      this.animation.setValue(toValue);
      return;
    }

    let interpolation: Animated.CompositeAnimation;
    if (this.props.type === 'spring') {
      interpolation = Animated.spring(this.animation, {
        toValue,
        useNativeDriver: this.useNativeDriver(),
      });
    } else {
      interpolation = Animated.timing(this.animation, {
        toValue,
        easing: this.props.easing,
        duration: this.props.duration,
        useNativeDriver: this.useNativeDriver(),
      });
    }

    if (this.props.loop) {
      let count = 0;
      this.loopingAnimation = AnimationUtil.loop(
        interpolation,
        // reset animation
        () => {
          this.animation.setValue(0);

          // Print a warning message if we seem to be looping a lot on web
          count += 1;
          if (WARN_LOOPING_ANIMATIONS && count > 4) {
            console.warn(
              'Performance warning: looping animations on ' +
                'web continuously is a performance drain!',
              count,
              Object.keys(this.props),
            );
          }
        },
      );

      this.loopingAnimation.start();
    } else {
      Animated.sequence([Animated.delay(this.props.delay || 0), interpolation]).start();
    }
  }

  interpolate = ({ from, to }: MBAnimationOutputRange<any>) =>
    this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [from, to],
    });

  render() {
    const {
      duration,
      style,
      opacity,
      scale,
      scaleX,
      scaleY,
      rotate,
      width,
      height,
      translateX,
      translateY,
      easing,
      backgroundColor,
      ...otherProps
    } = this.props;

    if (!!scale && (!!translateX || !!translateY)) {
      // For reference: https://stackoverflow.com/a/41833704/9117995
      throw new Error('Cannot translate and scale at the same time');
    }

    return (
      <Animated.View
        {...otherProps}
        style={[
          style,
          !!width && { width: this.interpolate(width) },
          !!height && { height: this.interpolate(height) },
          !!opacity && { opacity: this.interpolate(opacity) },
          !!backgroundColor && { backgroundColor: this.interpolate(backgroundColor) },
          !!scale && { transform: [{ scale: this.interpolate(scale) }] },
          !!scaleX && { transform: [{ scaleX: this.interpolate(scaleX) }] },
          !!scaleY && { transform: [{ scaleY: this.interpolate(scaleY) }] },
          !!rotate && { transform: [{ rotate: this.interpolate(rotate) }] },
          !!translateX && { transform: [{ translateX: this.interpolate(translateX) }] },
          !!translateY && { transform: [{ translateY: this.interpolate(translateY) }] },
        ]}
      />
    );
  }
}
